import React from 'react';
import { DollarSign, Wallet, Send, BarChart3, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  statusFromChartRows,
  sumRowValues,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';

export default function PayrollDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.dashboardPage.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/salaires/dashboard"
      title={p('title', { defaultValue: 'لوحة الرواتب والأجور' })}
      subtitle={p('subtitle', { defaultValue: 'مؤشرات الرواتب والسلف والتحويلات' })}
      icon={DollarSign}
      accent="from-lime-600 to-green-900"
      recentTitle={p('recent_payments', { defaultValue: 'آخر المدفوعات' })}
      quickLinks={[
        { label: p('link_payroll', { defaultValue: 'الرواتب' }), href: '/salaries/stats', icon: DollarSign },
        { label: p('link_loans', { defaultValue: 'سلف الموظفين' }), href: '/salaries/advances', icon: Wallet },
        { label: p('link_transfers', { defaultValue: 'التحويلات البنكية' }), href: '/salaries/virements-bancaires', icon: Send },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/salaries/analyses-rapports', icon: BarChart3 },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = data.chartRows || [];
        const { active, inactive } = statusFromChartRows(chartRows, [
          'processed',
          'paid',
          'complete',
          'approved',
        ]);

        return {
          hero: {
            label: p('kpi_total_payroll', { defaultValue: 'إجمالي الرواتب (الشهر)' }),
            value: formatBrandMoney(kpis.totalPayroll || 0),
            icon: DollarSign,
          },
          kpis: withKpiVariants([
            { label: p('kpi_loans', { defaultValue: 'سلف نشطة' }), value: kpis.activeLoans ?? 0, icon: Wallet },
            { label: p('kpi_transfers', { defaultValue: 'تحويلات معلقة' }), value: kpis.pendingTransfers ?? 0, icon: Send },
            { label: p('kpi_avg_salary', { defaultValue: 'متوسط الراتب' }), value: formatBrandMoney(kpis.avgSalary || 0), icon: BarChart3 },
            {
              label: p('kpi_payroll_items', { defaultValue: 'بنود الرواتب' }),
              value: sumRowValues(chartRows) || (kpis.activeLoans ?? 0) + (kpis.pendingTransfers ?? 0),
              icon: Users,
            },
          ]),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_payroll', { defaultValue: 'حالة كشوف الرواتب' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'سلف وتحويلات ومتوسط' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_loans', { defaultValue: 'سلف نشطة' }), value: kpis.activeLoans ?? 0 },
                { label: p('kpi_transfers', { defaultValue: 'تحويلات معلقة' }), value: kpis.pendingTransfers ?? 0 },
                { label: p('kpi_avg_salary', { defaultValue: 'متوسط الراتب' }), value: kpis.avgSalary ?? 0 },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'حالة الكشوف' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'معالجة مقابل معلقة' }),
              active: active || 0,
              inactive: inactive || kpis.pendingTransfers || 0,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_ranked_title', { defaultValue: 'توزيع حالات الرواتب' }),
              subtitle: p('chart_ranked_subtitle', { defaultValue: 'حسب الحالة' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
